/**
 * Unified notes redux slice — backs the shared NotesPanel component
 * across every surface that displays notes (Call Explorer Comments,
 * scorecard note, criteria note, inbox task drawer).
 *
 * Anchor keying: state is keyed by `${noteType}:${id}` so multiple
 * anchors can be cached side-by-side (e.g. a call's notes and a
 * scorecard's notes for the same call detail page).
 */

import { produce } from 'immer'

// Action Types
export const SET_NOTES = 'notes/setNotes'
export const APPEND_NOTE = 'notes/appendNote'
export const REPLACE_NOTE = 'notes/replaceNote'
export const REMOVE_NOTE = 'notes/removeNote'
export const SET_LOADING = 'notes/setLoading'
export const SET_FOR_CALL = 'notes/setForCall'

// Action Creators
export const setNotes = (payload) => ({ type: SET_NOTES, payload })
export const appendNote = (payload) => ({ type: APPEND_NOTE, payload })
export const replaceNote = (payload) => ({ type: REPLACE_NOTE, payload })
export const removeNote = (payload) => ({ type: REMOVE_NOTE, payload })
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setForCall = (payload) => ({ type: SET_FOR_CALL, payload })

// Helpers
export const anchorKey = (noteType, id) => `${noteType}:${id}`

// State
export const initialState = {
  // byAnchor[`${noteType}:${id}`] = Note[]
  byAnchor: {},
  // forCall[callId] = Note[] (union across all anchor types touching the call)
  forCall: {},
  // loading[`${noteType}:${id}`] = boolean (or 'for-call:${callId}')
  loading: {},
}

const reducer = (state = initialState, action) =>
  produce(state, (newState) => {
    const { payload } = action

    switch (action.type) {
      case SET_NOTES: {
        const { noteType, id, notes } = payload
        newState.byAnchor[anchorKey(noteType, id)] = notes
        break
      }
      case APPEND_NOTE: {
        const { noteType, id, note } = payload
        const key = anchorKey(noteType, id)
        newState.byAnchor[key] = [note, ...(state.byAnchor[key] || [])]
        break
      }
      case REPLACE_NOTE: {
        const { noteType, id, note } = payload
        const key = anchorKey(noteType, id)
        newState.byAnchor[key] = (state.byAnchor[key] || []).map((existingNote) =>
          existingNote.id === note.id ? note : existingNote
        )
        break
      }
      case REMOVE_NOTE: {
        const { noteType, id, noteId } = payload
        const key = anchorKey(noteType, id)
        newState.byAnchor[key] = (state.byAnchor[key] || []).filter(
          (existingNote) => existingNote.id !== noteId
        )
        break
      }
      case SET_FOR_CALL: {
        const { callId, notes } = payload
        newState.forCall[callId] = notes
        break
      }
      case SET_LOADING: {
        newState.loading = { ...state.loading, ...payload }
        break
      }
      default:
        break
    }
  })

export default reducer
